import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useTranslation } from "react-i18next";
import type { QrPlacement, QrPresetId } from "./qrCodeTemplates";
import { clampPlacement, DEFAULT_QR_PLACEMENT } from "./qrCodeTemplates";
import QrPresetLayer from "./QrPresetLayer";
import "./QrCodeDesigner.css";

export interface QrDesignCanvasHandle {
  getMeasuredPlacement: () => QrPlacement | null;
}

interface QrDesignCanvasProps {
  backgroundUrl: string | null;
  backgroundClassName?: string;
  presetId?: QrPresetId | null;
  restaurantName?: string;
  qrPreviewUrl: string | null;
  placement: QrPlacement;
  onPlacementChange: (placement: QrPlacement) => void;
  disabled?: boolean;
  aspectRatio?: number;
}

const MIN_SIZE = 8;
const MAX_SIZE = 60;

const QrDesignCanvas = forwardRef<QrDesignCanvasHandle, QrDesignCanvasProps>(
  function QrDesignCanvas(
    {
      backgroundUrl,
      backgroundClassName = "",
      presetId = null,
      restaurantName = "",
      qrPreviewUrl,
      placement,
      onPlacementChange,
      disabled = false,
      aspectRatio = 210 / 297,
    },
    ref
  ) {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLDivElement>(null);
    const qrRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{
      startX: number;
      startY: number;
      origin: QrPlacement;
    } | null>(null);
    const resizeRef = useRef<{
      startX: number;
      startY: number;
      origin: QrPlacement;
    } | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    useImperativeHandle(ref, () => ({
      getMeasuredPlacement: () => {
        const canvas = canvasRef.current;
        const qr = qrRef.current;
        if (!canvas || !qr) return null;

        const canvasRect = canvas.getBoundingClientRect();
        const qrRect = qr.getBoundingClientRect();
        if (canvasRect.width <= 0 || canvasRect.height <= 0) return null;

        return clampPlacement(
          {
            leftPercent: ((qrRect.left - canvasRect.left) / canvasRect.width) * 100,
            topPercent: ((qrRect.top - canvasRect.top) / canvasRect.height) * 100,
            sizePercent: (qrRect.width / canvasRect.width) * 100,
          },
          aspectRatio
        );
      },
    }));

    useEffect(() => {
      if (!isDragging) return undefined;

      const handleMove = (event: PointerEvent) => {
        const canvas = canvasRef.current;
        const drag = dragRef.current;
        if (!canvas || !drag) return;

        const rect = canvas.getBoundingClientRect();
        const deltaX = ((event.clientX - drag.startX) / rect.width) * 100;
        const deltaY = ((event.clientY - drag.startY) / rect.height) * 100;

        onPlacementChange(
          clampPlacement(
            {
              leftPercent: drag.origin.leftPercent + deltaX,
              topPercent: drag.origin.topPercent + deltaY,
              sizePercent: drag.origin.sizePercent,
            },
            aspectRatio
          )
        );
      };

      const handleUp = () => {
        dragRef.current = null;
        setIsDragging(false);
      };

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      return () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
      };
    }, [aspectRatio, isDragging, onPlacementChange]);

    useEffect(() => {
      if (!isResizing) return undefined;

      const handleMove = (event: PointerEvent) => {
        const canvas = canvasRef.current;
        const resize = resizeRef.current;
        if (!canvas || !resize) return;

        const rect = canvas.getBoundingClientRect();
        const deltaX = ((event.clientX - resize.startX) / rect.width) * 100;
        const deltaY = ((event.clientY - resize.startY) / rect.height) * 100;
        const delta = (deltaX + deltaY) / 2;

        onPlacementChange(
          clampPlacement(
            {
              ...resize.origin,
              sizePercent: resize.origin.sizePercent + delta,
            },
            aspectRatio
          )
        );
      };

      const handleUp = () => {
        resizeRef.current = null;
        setIsResizing(false);
      };

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      return () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
      };
    }, [aspectRatio, isResizing, onPlacementChange]);

    const safePlacement = clampPlacement(placement, aspectRatio);
    const isBusy = isDragging || isResizing;

    const adjustSize = (delta: number) => {
      onPlacementChange(
        clampPlacement(
          {
            ...safePlacement,
            sizePercent: safePlacement.sizePercent + delta,
          },
          aspectRatio
        )
      );
    };

    const resetPlacement = () => {
      onPlacementChange(DEFAULT_QR_PLACEMENT);
    };

    const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
      if (disabled || !qrPreviewUrl) return;
      event.preventDefault();
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        origin: safePlacement,
      };
      setIsDragging(true);
    };

    const startResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (disabled || !qrPreviewUrl) return;
      event.preventDefault();
      event.stopPropagation();
      resizeRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        origin: safePlacement,
      };
      setIsResizing(true);
    };

    return (
      <div className="qr-design-canvas-wrap">
        <div className="qr-design-canvas__hint">{t("qrDesignDragHint")}</div>
        <div
          ref={canvasRef}
          className={`qr-design-canvas ${backgroundClassName}`.trim()}
          style={{ aspectRatio }}
        >
        {backgroundUrl ? (
          <img src={backgroundUrl} alt="" className="qr-design-canvas__bg" />
        ) : presetId ? (
          <QrPresetLayer presetId={presetId} restaurantName={restaurantName} />
        ) : null}

          {qrPreviewUrl ? (
            <div
              ref={qrRef}
              className={`qr-design-canvas__qr${isBusy ? " is-busy" : ""}`}
              style={{
                left: `${safePlacement.leftPercent}%`,
                top: `${safePlacement.topPercent}%`,
                width: `${safePlacement.sizePercent}%`,
              }}
            >
              <div
                className="qr-design-canvas__qr-move"
                onPointerDown={startDrag}
                role="presentation"
              >
                <img src={qrPreviewUrl} alt="" draggable={false} />
                <span className="qr-design-canvas__qr-grip" aria-hidden="true">
                  <i className="fa fa-arrows-alt" />
                </span>
              </div>
              <button
                type="button"
                className="qr-design-canvas__resize-handle"
                onPointerDown={startResize}
                disabled={disabled}
                aria-label={t("qrDesignResizeHandle")}
              >
                <i className="fa fa-up-right-and-down-left-from-center" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="qr-design-canvas__placeholder">{t("qrDesignPreviewLoading")}</div>
          )}
        </div>

        <div className="qr-design-canvas__controls">
          <div className="qr-design-canvas__size">
            <label htmlFor="qr-size-range">{t("qrDesignSizeLabel")}</label>
            <div className="qr-design-canvas__size-row">
              <button
                type="button"
                className="qr-design-canvas__size-btn"
                onClick={() => adjustSize(-2)}
                disabled={disabled || !qrPreviewUrl || safePlacement.sizePercent <= MIN_SIZE}
                aria-label={t("qrDesignSizeSmaller")}
              >
                <i className="fa fa-minus" />
              </button>
              <input
                id="qr-size-range"
                type="range"
                min={MIN_SIZE}
                max={MAX_SIZE}
                step={1}
                value={safePlacement.sizePercent}
                disabled={disabled || !qrPreviewUrl}
                onChange={(e) =>
                  onPlacementChange(
                    clampPlacement(
                      {
                        ...safePlacement,
                        sizePercent: Number(e.target.value),
                      },
                      aspectRatio
                    )
                  )
                }
              />
              <button
                type="button"
                className="qr-design-canvas__size-btn"
                onClick={() => adjustSize(2)}
                disabled={disabled || !qrPreviewUrl || safePlacement.sizePercent >= MAX_SIZE}
                aria-label={t("qrDesignSizeBigger")}
              >
                <i className="fa fa-plus" />
              </button>
              <span className="qr-design-canvas__size-value">
                {Math.round(safePlacement.sizePercent)}%
              </span>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary qr-design-canvas__reset"
            onClick={resetPlacement}
            disabled={disabled || !qrPreviewUrl}
          >
            {t("qrDesignResetPosition")}
          </button>
        </div>
      </div>
    );
  }
);

export default QrDesignCanvas;
